/**
 * Estados de la cotización NBS. Deben coincidir con el enum EstadoCotizacion del back
 * (Conekt-CotizadorNbs/src/Domain/Cotizador/Enums/EstadoCotizacion.cs) y con el seed de
 * EstadoCotizacionNBS: los valores empiezan en 1.
 */
export const EnumEstadoCotizacionNBS = {
    Borrador: 1,
    RevisionInterna: 2,
    RevisionCliente: 3,
    Aceptada: 4,
    Rechazada: 5,
} as const;

export type EstadoCotizacionNBSId = typeof EnumEstadoCotizacionNBS[keyof typeof EnumEstadoCotizacionNBS];

const NOMBRES_ESTADO: Record<number, string> = {
    [EnumEstadoCotizacionNBS.Borrador]: 'Borrador',
    [EnumEstadoCotizacionNBS.RevisionInterna]: 'Revisión Interna',
    [EnumEstadoCotizacionNBS.RevisionCliente]: 'Revisión Cliente',
    [EnumEstadoCotizacionNBS.Aceptada]: 'Aceptada',
    [EnumEstadoCotizacionNBS.Rechazada]: 'Rechazada',
};

/** Nombre para mostrar a partir del id del estado que envía el back. */
export const nombreEstadoCotizacion = (estadoId?: number | null) =>
    (estadoId != null && NOMBRES_ESTADO[estadoId]) || 'Desconocido';

// Hex explícitos (misma paleta que los estados de Tareas, enumEstadoTarea.ts): el tema usa
// primaryShade: 0, así que con nombres de color ('gray', 'blue'…) Mantine toma el tono más claro.
const GRIS = '#95a5a6';
const COLORES_ESTADO: Record<number, string> = {
    [EnumEstadoCotizacionNBS.Borrador]: GRIS,
    [EnumEstadoCotizacionNBS.RevisionInterna]: '#0074d9', // azul
    [EnumEstadoCotizacionNBS.RevisionCliente]: '#9b59b6', // morado
    [EnumEstadoCotizacionNBS.Aceptada]: '#5fd373',        // verde
    [EnumEstadoCotizacionNBS.Rechazada]: '#f58585',       // rojo
};

/** Color de cada estado (uno distinto por estado). */
export const colorEstadoCotizacion = (estadoId?: number | null) =>
    (estadoId != null && COLORES_ESTADO[estadoId]) || GRIS;

/** El PDF se puede generar en cualquier estado distinto de Borrador (la cotización ya está calculada). */
export const puedeGenerarPdf = (estadoId?: number | null) =>
    estadoId != null && estadoId !== EnumEstadoCotizacionNBS.Borrador;

export interface IAccionEstadoCotizacion {
    estado: EstadoCotizacionNBSId;
    etiqueta: string;
    /** Texto del diálogo de confirmación. */
    confirmacion: string;
    color: string;
}

/**
 * Acción que se muestra para pasar de `desde` a `hacia`. Las transiciones válidas las envía el back
 * (TransicionesPermitidas); aquí solo se definen los textos. Mismos textos en el formulario y en la fila.
 */
export const accionTransicionEstado = (_desde: number | undefined, hacia: number): IAccionEstadoCotizacion => {
    switch (hacia) {
        case EnumEstadoCotizacionNBS.RevisionInterna:
            return { estado: hacia, etiqueta: 'Revisión Interna', color: colorEstadoCotizacion(hacia), confirmacion: 'La cotización pasará a Revisión Interna. ¿Deseas continuar?' };
        case EnumEstadoCotizacionNBS.RevisionCliente:
            return { estado: hacia, etiqueta: 'Revisión Cliente', color: colorEstadoCotizacion(hacia), confirmacion: 'La cotización pasará a Revisión Cliente. ¿Deseas continuar?' };
        case EnumEstadoCotizacionNBS.Aceptada:
            return { estado: hacia, etiqueta: 'Aceptada', color: colorEstadoCotizacion(hacia), confirmacion: 'Una cotización aceptada queda en solo lectura: no se podrá modificar, recalcular, eliminar ni cambiar de estado. ¿Deseas marcarla como Aceptada?' };
        case EnumEstadoCotizacionNBS.Rechazada:
            return { estado: hacia, etiqueta: 'Rechazada', color: colorEstadoCotizacion(hacia), confirmacion: 'La cotización pasará a Rechazada. ¿Deseas continuar?' };
        default:
            return { estado: hacia as EstadoCotizacionNBSId, etiqueta: nombreEstadoCotizacion(hacia), color: colorEstadoCotizacion(hacia), confirmacion: '¿Deseas cambiar el estado de la cotización?' };
    }
};

/** Texto de la alerta de la documentación cuando la configuración global cambió. */
export const MENSAJE_CONFIGURACION_ACTUALIZADA =
    'Las variables globales del sistema han sido actualizadas y las condiciones del negocio han variado frente al borrador original.';
